/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  Edit3,
  CheckCircle,
  AlertCircle,
  Shield,
  Activity,
  X,
} from "lucide-react";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import FileUpload from "./FileUpload";
import toast from "react-hot-toast";
import EmailOTPAuthentication from "../../Login/OTPAuth/EmailOTPAuthentication";
import person from "../Media/person-svgrepo-com (2).png";
import empty from "../../../../src/Assets/Media/Empty.png";
import { CustomButtonGroup } from "../../../Assets/Elements";
import PropTypes from "prop-types";

function Profile() {
  const { loginUser, formatDate } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleNavigation = () => {
    navigate("/Dashboard/EditAccountDetails");
  };

  const isVerified =
    loginUser.isEmailVerified &&
    loginUser.isMobileVerified &&
    loginUser.hasPaidInitialFee;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Gold Pattern Background - 80px height */}
          <div className="h-32 bg-gradient-to-r from-[#b99653] to-[#d4af37] relative overflow-hidden">
            {/* Decorative Pattern */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            {/* Edit Button */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
              <button
                onClick={handleNavigation}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg transition-all duration-200 text-white font-semibold text-sm shadow-lg"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            </div>
          </div>

          {/* Profile Content - White Section */}
          <div className="relative">
            {/* Profile Image - Overlapping */}
            <div className="absolute -top-16 left-6">
              <div
                className="relative cursor-pointer group"
                onClick={() => setShowModal(true)}
              >
                <div className="w-32 h-32 rounded-full bg-white p-1.5 shadow-xl ring-4 ring-white">
                  <img
                    src={loginUser.profile_image || person}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center">
                  <Edit3 className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            {/* Profile Info Section */}
            <div className="pt-20 pb-6 px-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {loginUser.firstName} {loginUser.lastName}
                  </h1>
                  {isVerified ? (
                    <div
                      id="account-verification-done"
                      className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-500 flex-shrink-0"
                    >
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div
                      id="account-verification-not-done"
                      className="flex items-center justify-center w-7 h-7 rounded-full bg-yellow-500 flex-shrink-0"
                    >
                      <AlertCircle className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                <ReactTooltip
                  anchorId="account-verification-done"
                  place="right"
                  content="Account is verified"
                  className="!bg-blue-500"
                />
                <ReactTooltip
                  anchorId="account-verification-not-done"
                  place="right"
                  content="Account verification is needed"
                  className="!bg-yellow-500"
                />

                <UpdateDescriptionSpecialitiesForm loginUser={loginUser} />

                {/* Specialities Tags */}
                {loginUser.specialities &&
                  loginUser.specialities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {loginUser.specialities.map((item, index) => (
                        <span
                          key={index}
                          className="px-4 py-1.5 bg-gradient-to-r from-[#fcdb6a]/30 to-[#b99653]/20 text-[#101c4c] text-sm font-semibold rounded-full border border-[#b99653]/30"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Profile Details
              </h2>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {/* Full Name */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <User className="w-5 h-5 text-[#b99653]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 font-medium">Full Name</p>
                  <p className="text-base font-semibold text-gray-900 mt-0.5">
                    {loginUser.firstName} {loginUser.lastName}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Mail className="w-5 h-5 text-[#b99653]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 font-medium">
                    Email Address
                  </p>
                  <div className="mt-0.5">
                    <EmailOTPAuthentication />
                  </div>
                </div>
              </div>

              {/* Mobile */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Phone className="w-5 h-5 text-[#b99653]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 font-medium">
                    Mobile Number
                  </p>
                  <div className="mt-0.5">
                    <p className="text-sm text-gray-900">
                      {loginUser.mobile_number || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Created */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Calendar className="w-5 h-5 text-[#b99653]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 font-medium">
                    Account Created
                  </p>
                  <p className="text-base font-semibold text-gray-900 mt-0.5">
                    {loginUser.DateCreated
                      ? formatDate(loginUser.DateCreated)
                      : "4th July, 2023, 08.21 PM IST"}
                  </p>
                </div>
              </div>

              {/* Last Login */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Clock className="w-5 h-5 text-[#b99653]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 font-medium">
                    Last Login
                  </p>
                  <p className="text-base font-semibold text-gray-900 mt-0.5">
                    {loginUser.LastLogin && loginUser.LastLogin.length > 0
                      ? formatDate(
                          loginUser.LastLogin[loginUser.LastLogin.length - 1]
                            .attempt
                        )
                      : "Details unavailable"}
                  </p>
                  <LoginHistory loginUser={loginUser} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Image Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                Update Profile Picture
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <FileUpload userId={loginUser._id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;

// Login History Component
LoginHistory.propTypes = {
  loginUser: PropTypes.object.isRequired,
};

export function LoginHistory({ loginUser }) {
  const [filterType, setFilterType] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const { formatDate } = useContext(AuthContext);

  const sortedLastLogin = loginUser.LastLogin
    ? [...loginUser.LastLogin].sort(
        (a, b) => new Date(b.attempt) - new Date(a.attempt)
      )
    : [];

  const filteredLogins = sortedLastLogin.filter((login) => {
    if (filterType === "All") return true;
    return login.status === filterType;
  });

  const buttons = [
    { label: "All", value: "All", onClick: () => setFilterType("All") },
    {
      label: "Success",
      value: "success",
      onClick: () => setFilterType("success"),
    },
    {
      label: "Failed",
      value: "failed",
      onClick: () => setFilterType("failed"),
    },
  ];

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="mt-2 text-sm text-[#b99653] hover:text-[#d4af37] font-semibold hover:underline transition-colors"
      >
        View Login History
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#b99653] to-[#d4af37] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Login History
                    </h3>
                    <p className="text-sm text-white/80">
                      {loginUser?.username || "Guest"} ({filteredLogins.length}{" "}
                      logins)
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex gap-2">
                <CustomButtonGroup buttons={buttons} filterType={filterType} />
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              {filteredLogins.length > 0 ? (
                <div className="space-y-2">
                  {filteredLogins.map((login, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      } hover:bg-gray-100`}
                    >
                      <div
                        className={`flex-shrink-0 w-2 h-2 rounded-full ${
                          login.status === "success"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      />
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <p
                            className={`text-sm font-semibold ${
                              login.status === "success"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {login.status
                              ? login.status.charAt(0).toUpperCase() +
                                login.status.slice(1)
                              : "Unknown"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Time</p>
                          <p className="text-sm font-medium text-gray-900">
                            {login.attempt
                              ? formatDate(login.attempt)
                              : "No date available"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Comment</p>
                          <p className="text-sm text-gray-700">
                            {login.reason || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <img src={empty} className="w-48 mb-4" alt="No data" />
                  <p className="text-gray-500">No login history to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Update Description Component
const UpdateDescriptionSpecialitiesForm = ({ loginUser }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { updateDescriptionSpecialities } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [description, setDescription] = useState(loginUser.description || "");
  const [speciality1, setSpeciality1] = useState(
    loginUser.specialities ? loginUser.specialities[0] || "" : ""
  );
  const [speciality2, setSpeciality2] = useState(
    loginUser.specialities ? loginUser.specialities[1] || "" : ""
  );
  const [speciality3, setSpeciality3] = useState(
    loginUser.specialities ? loginUser.specialities[2] || "" : ""
  );
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    const isChanged =
      description !== loginUser.description ||
      description !== "" ||
      speciality1 !==
        (loginUser.specialities ? loginUser.specialities[0] || "" : "") ||
      speciality2 !==
        (loginUser.specialities ? loginUser.specialities[1] || "" : "") ||
      speciality3 !==
        (loginUser.specialities ? loginUser.specialities[2] || "" : "");

    setIsButtonEnabled(isChanged);
  }, [description, speciality1, speciality2, speciality3, loginUser]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubmit = async () => {
    if (!description && !speciality1 && !speciality2 && !speciality3) {
      toast.error("Please enter at least one description or speciality.");
      return;
    }

    const specialities = [speciality1, speciality2, speciality3].filter(
      Boolean
    );

    if (!description && specialities.length === 0) {
      toast.error("Description and at least one speciality are required.");
      return;
    }

    try {
      setIsLoading(true);
      await updateDescriptionSpecialities(
        loginUser._id,
        description,
        specialities
      );
      handleClose();
      setIsLoading(false);
    } catch (error) {
      toast.error(`Failed to update. Please try again. error: ${error}`);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <p
        className="text-gray-600 cursor-pointer hover:text-[#b99653] transition-colors flex items-center gap-2 mt-2 text-base"
        onClick={handleClick}
        title="Click to update your description and specialities"
      >
        {loginUser.description ? (
          <span>
            {loginUser.description}{" "}
            <Edit3 className="inline w-4 h-4 text-[#b99653] ml-1" />
          </span>
        ) : (
          <span className="italic text-gray-500">
            Say something about yourself{" "}
            <Edit3 className="inline w-4 h-4 text-[#b99653] ml-1" />
          </span>
        )}
      </p>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(185, 150, 83, 0.2)",
          },
        }}
      >
        <div style={{ padding: "20px", maxWidth: "350px" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Update Profile
          </Typography>
          <TextField
            label="Description"
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            size="small"
            multiline
            rows={4}
          />
          <TextField
            label="Speciality 1"
            fullWidth
            value={speciality1}
            onChange={(e) => setSpeciality1(e.target.value)}
            margin="normal"
            size="small"
          />
          <TextField
            label="Speciality 2"
            fullWidth
            value={speciality2}
            onChange={(e) => setSpeciality2(e.target.value)}
            margin="normal"
            size="small"
          />
          <TextField
            label="Speciality 3"
            fullWidth
            value={speciality3}
            onChange={(e) => setSpeciality3(e.target.value)}
            margin="normal"
            size="small"
          />
          <button
            onClick={
              isButtonEnabled
                ? handleSubmit
                : () => toast.error("Please enter any details to update!")
            }
            disabled={!isButtonEnabled}
            className={`w-full mt-4 px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
              isButtonEnabled
                ? "bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white hover:shadow-lg"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isLoading ? "Updating..." : "Update"}
          </button>
        </div>
      </Popover>
    </div>
  );
};
